import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Plus, Edit3, Trash2, X, Save, Loader2,
  Eye, EyeOff, Pencil, Lock, Filter, ChevronDown, ChevronRight
} from 'lucide-react';
import apiClient from '../utils/apiClient';

const ACCIONES_LABELS = {
  ver: 'Ver',
  editar: 'Editar',
  crear: 'Crear',
  eliminar: 'Eliminar',
};

const ACCIONES_ICONS = {
  ver: <Eye size={13} />,
  editar: <Pencil size={13} />,
  crear: <Plus size={13} />,
  eliminar: <Trash2 size={13} />,
};

// ===============================================================
// EDITOR DE PERMISOS
// ===============================================================
const PermisosEditor = ({ permisos, onChange, modulosDisponibles, clientesConfig }) => {
  const modulos = permisos?.modulos || {};
  const filtros = permisos?.filtros || {};

  const toggleModulo = (key) => {
    const current = modulos[key];
    if (current) {
      const updated = { ...modulos };
      delete updated[key];
      onChange({ ...permisos, modulos: updated });
    } else {
      const mod = modulosDisponibles.find(m => m.key === key);
      const acciones = {};
      (mod?.acciones || ['ver']).forEach(a => { acciones[a] = a === 'ver'; });
      onChange({ ...permisos, modulos: { ...modulos, [key]: acciones } });
    }
  };

  const toggleAccion = (modKey, accion) => {
    const current = modulos[modKey] || {};
    onChange({
      ...permisos,
      modulos: { ...modulos, [modKey]: { ...current, [accion]: !current[accion] } }
    });
  };

  const updateFiltro = (key, value) => {
    onChange({ ...permisos, filtros: { ...filtros, [key]: value } });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-2">Modulos y permisos</p>

      {modulosDisponibles.map(mod => {
        const isActive = !!modulos[mod.key];
        const acciones = modulos[mod.key] || {};

        return (
          <div key={mod.key} className={`rounded-xl border transition-all ${isActive ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'}`}>
            <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => toggleModulo(mod.key)}>
              <div className="flex items-center gap-2">
                <div className={`w-5 h-5 rounded-md flex items-center justify-center text-xs ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-400'}`}>
                  {isActive ? '✓' : ''}
                </div>
                <span className={`text-sm font-medium ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>{mod.label}</span>
              </div>
              {isActive && <ChevronDown size={14} className="text-gray-400" />}
            </div>

            {isActive && (
              <div className="px-3 pb-3 flex flex-wrap gap-2">
                {mod.acciones.map(accion => (
                  <button key={accion} onClick={() => toggleAccion(mod.key, accion)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${acciones[accion] ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'}`}>
                    {ACCIONES_ICONS[accion]} {ACCIONES_LABELS[accion]}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Filtros de datos */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-3 flex items-center gap-1">
          <Filter size={12} /> Filtros de datos (opcional)
        </p>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!filtros.solo_mis_casos}
                onChange={e => updateFiltro('solo_mis_casos', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Solo ver sus propios casos</span>
            </label>
          </div>

          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Filtrar por aseguradora</label>
            <select multiple value={filtros.aseguradora || []}
              onChange={e => updateFiltro('aseguradora', Array.from(e.target.selectedOptions, o => o.value))}
              className="w-full p-2 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 h-20">
              {clientesConfig.map(c => (
                <option key={c.slug} value={c.nombre_display}>{c.nombre_display}</option>
              ))}
            </select>
            <p className="text-[10px] text-gray-400 mt-1">Ctrl+click para seleccionar varias. Vacio = todas</p>
          </div>

          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Filtrar tipo de servicio (separar por coma)</label>
            <input value={(filtros.tipo_servicio || []).join(', ')}
              onChange={e => updateFiltro('tipo_servicio', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              placeholder="Ej: PRELIMINAR, SIMPLE"
              className="w-full p-2 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600" />
          </div>

          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Filtrar cierre de servicio (separar por coma)</label>
            <input value={(filtros.cierre_servicio || []).join(', ')}
              onChange={e => updateFiltro('cierre_servicio', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              placeholder="Ej: TELEFONICO, SITIO"
              className="w-full p-2 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ===============================================================
// MODAL CREAR/EDITAR ROL
// ===============================================================
const RolModal = ({ rol, onClose, onSaved, modulosDisponibles, clientesConfig }) => {
  const isEdit = !!rol?.id;
  const [form, setForm] = useState({
    nombre_rol: rol?.nombre_rol || '',
    descripcion: rol?.descripcion || '',
    permisos: rol?.permisos || { modulos: {}, filtros: {} },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!form.nombre_rol.trim()) { setError('El nombre del rol es obligatorio'); return; }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await apiClient.put(`/roles/${rol.id}`, form);
      } else {
        await apiClient.post('/roles', form);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error guardando rol');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-2xl border border-gray-200 dark:border-gray-700 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield size={20} className="text-blue-500" />
            {isEdit ? 'Editar Rol' : 'Nuevo Rol'}
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre del rol *</label>
              <input value={form.nombre_rol} onChange={e => setForm(p => ({ ...p, nombre_rol: e.target.value }))}
                placeholder="Ej: Usuario Equidad"
                className="w-full p-3 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descripcion</label>
              <input value={form.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))}
                placeholder="Breve descripcion del rol"
                className="w-full p-3 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600" />
            </div>
          </div>

          <PermisosEditor
            permisos={form.permisos}
            onChange={p => setForm(prev => ({ ...prev, permisos: p }))}
            modulosDisponibles={modulosDisponibles}
            clientesConfig={clientesConfig}
          />
        </div>

        {error && <p className="text-red-500 text-sm mt-3 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">{error}</p>}

        <div className="flex gap-3 mt-6">
          <button onClick={handleSubmit} disabled={saving || !form.nombre_rol.trim()}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl disabled:opacity-50 transition-all flex items-center justify-center gap-2">
            <Save size={16} /> {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear Rol'}
          </button>
          <button onClick={onClose} className="px-6 py-3 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-xl">Cancelar</button>
        </div>
      </div>
    </div>
  );
};

// ===============================================================
// COMPONENTE PRINCIPAL
// ===============================================================
const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [modulosDisponibles, setModulosDisponibles] = useState([]);
  const [clientesConfig, setClientesConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRol, setEditingRol] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, modulosRes, clientesRes] = await Promise.all([
        apiClient.get('/roles'),
        apiClient.get('/roles/modulos-disponibles'),
        apiClient.get('/clientes-config'),
      ]);
      setRoles(rolesRes.data);
      setModulosDisponibles(modulosRes.data);
      setClientesConfig(clientesRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (rol) => {
    if (!window.confirm(`Desactivar el rol "${rol.nombre_rol}"?`)) return;
    try {
      await apiClient.delete(`/roles/${rol.id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Error');
    }
  };

  const countPermisos = (permisos) => {
    const modulos = permisos?.modulos || {};
    return Object.keys(modulos).length;
  };

  return (
    <div className="p-4 md:p-8 bg-slate-100 dark:bg-gray-950 min-h-screen overflow-y-auto">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield size={28} className="text-blue-500" /> Gestion de Roles
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Define los permisos y filtros para cada tipo de usuario</p>
        </div>
        <button onClick={() => { setEditingRol(null); setModalOpen(true); }}
          className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-6 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg">
          <Plus size={20} /> Nuevo Rol
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 size={48} className="animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 pb-12">
          {roles.map(rol => {
            const modulos = rol.permisos?.modulos || {};
            const filtros = rol.permisos?.filtros || {};
            const moduloKeys = Object.keys(modulos);

            return (
              <div key={rol.id} className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{rol.nombre_rol}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{rol.descripcion || 'Sin descripcion'}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingRol(rol); setModalOpen(true); }}
                      className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-colors">
                      <Edit3 size={16} />
                    </button>
                    {rol.nombre_rol !== 'Administrador' && (
                      <button onClick={() => handleDelete(rol)}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Modulos */}
                <div className="space-y-1.5 mb-3">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{moduloKeys.length} modulo{moduloKeys.length !== 1 ? 's' : ''}</p>
                  {moduloKeys.map(key => {
                    const acciones = modulos[key];
                    const accionesActivas = Object.entries(acciones).filter(([, v]) => v).map(([k]) => k);
                    return (
                      <div key={key} className="flex items-center justify-between text-xs">
                        <span className="text-gray-700 dark:text-gray-300 font-medium capitalize">{key.replace('_', ' ')}</span>
                        <div className="flex gap-1">
                          {accionesActivas.map(a => (
                            <span key={a} className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-[10px] font-medium">
                              {a}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Filtros activos */}
                {(filtros.solo_mis_casos || filtros.aseguradora?.length || filtros.tipo_servicio?.length) && (
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1 flex items-center gap-1"><Filter size={10} /> Filtros</p>
                    <div className="flex flex-wrap gap-1">
                      {filtros.solo_mis_casos && <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-[10px]">Solo sus casos</span>}
                      {filtros.aseguradora?.map(a => <span key={a} className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-[10px]">{a}</span>)}
                      {filtros.tipo_servicio?.map(t => <span key={t} className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded text-[10px]">{t}</span>)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <RolModal
          rol={editingRol}
          onClose={() => setModalOpen(false)}
          onSaved={fetchData}
          modulosDisponibles={modulosDisponibles}
          clientesConfig={clientesConfig}
        />
      )}
    </div>
  );
};

export default Roles;
